import { SecurityGroup } from 'api/types';

export interface MarketHistoryDataPoint {
    price: number;
    date: string;
}

/** Standard solution Security type codes.*/
export enum SecurityTypeCode {
    EQUITY = 'E', //Equity
    ETF = 'CE', //Exchange-traded funds (ETFs)
    PRIVATE_EQUITY = 'PE', //Private Equity
    DEBT_INSTRUMENT = 'D', //Debt instruments
    COLLECTIVE_INVESTMENT_VEHICLE = 'C', //Collective investment vehicles
    CURRENCY = 'TC', //Currencies
    DEPOSIT_INSTRUMENT = 'LD', //Deposit instruments
    FORWARD_CASHFLOW = 'FUTCF', //Forward cashflow
}

/**
 * Security tags indicating whether a security can be bought or sold
 * in units and/or trade amount.
 */
export enum SecurityTradeType {
    buyUnits = 'Trade type:Buy units',
    buyTradeAmount = 'Trade type:Buy trade amount',
    sellUnits = 'Trade type:Sell units',
    sellTradeAmount = 'Trade type:Sell trade amount',
}

export interface SecurityDetailsPosition {
    id: number;
    name: string;
    namesAsMap: Record<string, string>;
    securityCode: string;
    isinCode: string;
    url: string;
    url2: string;
    currency: {
        securityCode: string;
        amountDecimalCount: number;
    };
    latestMarketData?: {
        price: number;
        date: string;
    };
    type: {
        code: SecurityTypeCode;
        namesAsMap: Record<string, string>;
        name: string;
    };
    tagsAsSet: string[];
    documents: {
        fileName: string;
        identifier: string;
        mimeType: string;
    }[];
    amountDecimalCount: number;
    groups: SecurityGroup[];
}

export interface SecurityDetailsQuery {
    security: SecurityDetailsPosition;
    transactionsByParameters?: TransactionDate[];
}

export interface TransactionDate {
    transactionDate: string;
}

export interface SecurityMarketDataHistory {
    marketDataHistory: MarketHistoryDataPoint[];
}

export interface SecurityMarketDataHistoryQuery {
    security: SecurityMarketDataHistory;
}

export interface HoldingPosition {
    security: {
        id: number;
    };
    amount: number;
    purchaseTradeAmount: number;
    marketValue: number;
    marketFxRate: number;
}

export interface PortfolioHoldingDetailsQuery {
    portfolio: {
        portfolioReport: {
            holdingPositions: HoldingPosition[];
        };
    };
}

export interface ContactHoldingDetailsQuery {
    contact: {
        id: number;
        portfolios: {
            id: number;
            portfolioReport: {
                holdingPositions: HoldingPosition[];
            };
        }[];
    };
}

export interface AnalyticsSecurityTypeData {
    code: string;
    name: string;
    firstAnalysis: {
        marketValue: number;
        tradeAmount: number;
        shareOfTotal: number;
        [key: string]: number;
    };
}

export interface AnalyticsSecurityData {
    security: {
        id: number;
        isinCode: string;
        countryCode: string;
        currencyCode: string;
        tagsAsList: string[];
        securityTypeCode: string;
        groups: SecurityGroup[];
        currencyName: string;
        maturityDate: string;
    };
    code: string;
    name: string;
    firstAnalysis: {
        marketValue: number;
        tradeAmount: number;
        amount: number;
        accruedInterest: number;
        purchaseTradeAmount: number;
        [key: string]: number;
    };
}

export interface AnalyticsSecurityTypeDataWithSecurityData extends AnalyticsSecurityTypeData {
    securities: AnalyticsSecurityData[];
}

export interface AnalyticsPortfolioHoldingsData {
    portfolio: {
        id: number;
    };
    firstAnalysis: {
        marketValue: number;
        tradeAmount: number;
        [key: string]: number;
    };
    securityTypes: AnalyticsSecurityTypeData[];
}

export interface ContactHoldingsFromAnalyticsQuery {
    contact: {
        id: number;
        analytics: {
            contact: {
                firstAnalysis: {
                    marketValue: number;
                    tradeAmount: number;
                    [key: string]: number;
                };
                securityTypes: AnalyticsSecurityTypeDataWithSecurityData[];
                parentPortfolios: PortfolioData[];
            };
        };
    };
}

export interface PortfolioData {
    portfolio: {
        id: number;
    };
    firstAnalysis: {
        marketValue: number;
        tradeAmount: number;
        [key: string]: number;
    };
    securityTypes: AnalyticsSecurityTypeDataWithSecurityData[];
}

export interface ContactOverviewQuery {
    contact: {
        id: number;
        analytics: {
            contact: {
                firstAnalysis: {
                    marketValue: number;
                    tradeAmount: number;
                    [key: string]: number;
                };
                parentPortfolios: PortfolioData[];
            };
        };
    };
}

export interface PortfolioHoldingsFromAnalyticsQuery {
    analytics: {
        grouppedAnalytics: AnalyticsPortfolioHoldingsData;
    };
}
